// src/components/roads/RoadReportSelectorTable.js
// --- UPDATE (Road Works Report tab) -------------------------------------------
// Road selector + network report table for the Road Works Report tab.
// Based on the RoadReportSelectorTable spec (search, constituency filter, status,
// condition, active-row indicator) and extended with:
//   • Segments   — how many stations the road is sliced into, length, potholes, poor segments
//   • ML         — length-weighted segment PCI, worst segment, engine (ml / rules / mixed),
//                  model treatment and when it last ran (ML segment model road-pci-1.1)
//   • AI         — remaining-life prediction (RSF model or labelled estimate), risk band,
//                  flood alert, insights count — same numbers as the inventory AI column
//   • AI priority — risk index + segment/WO/QA/inspection signals → score, reasons and the
//                  next best action (click runs it: open report / map road / run AI)
//   • Work orders (open · critical · overdue · QA pending), last QA result, inspections
//   • Header KPIs, quick filters, sortable columns, CSV export, inventory paging
// Data: the page's inventory roads + ONE GET /roads/report-summary?ids=… per page (no N+1).
// Live: road / work-order / asphalt events refresh the roll-up (debounced); stale responses
// are dropped; summary errors show a retry banner while the road columns stay usable.
// --- END UPDATE ---------------------------------------------------------------
import React, { useState, useMemo, useEffect, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Table, Thead, Tbody, Tr, Th, Td, Input, InputGroup, InputLeftElement,
    HStack, VStack, Badge, Text, Icon, useColorModeValue, Flex, Select, Tooltip, Button,
    ButtonGroup, IconButton, Progress, Skeleton, Alert, AlertIcon, AlertDescription, SimpleGrid,
} from '@chakra-ui/react';
import { FaRoad } from 'react-icons/fa';
import {
    MdCheckCircle, MdLocationOn, MdSearch, MdRefresh, MdFileDownload, MdArrowUpward, MdArrowDownward,
    MdAutoAwesome, MdMap, MdVisibility, MdWaterDrop, MdChevronLeft, MdChevronRight,
} from 'react-icons/md';
import { apiFetch } from './page/shared';
import useFlowSenseLive from '../../hooks/useFlowSenseLive';
import {
    buildRoadReportRow, summarizeReportRows, sortReportRows, reportRowsToCsv,
    REPORT_FILTERS, REPORT_INTENTS, pciColor, relDays,
} from './roadReportIntel';

const RATING_SCHEMES = { excellent: 'green', good: 'teal', fair: 'yellow', poor: 'orange', critical: 'red' };
const STATUS_SCHEMES = {
    operational: 'green', maintenance_required: 'orange', under_maintenance: 'blue',
    closed: 'red', planned: 'purple', decommissioned: 'gray',
};
const QA_SCHEMES = { approved: 'green', flagged_for_penalty: 'yellow', rework_mandated: 'red' };
const QA_LABEL = { approved: 'QA approved', flagged_for_penalty: 'QA flagged', rework_mandated: 'QA rework' };
const MAX_SUMMARY_IDS = 100;
const LIVE_DEBOUNCE_MS = 2000;

const SortTh = memo(({ col, label, sortKey, sortDir, onSort, ...rest }) => {
    const active = sortKey === col;
    return (
        <Th {...rest} cursor="pointer" userSelect="none" onClick={() => onSort(col)} title={`Sort by ${label}`}
            aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <HStack spacing={1}>
                <Text as="span">{label}</Text>
                {active && <Icon as={sortDir === 'asc' ? MdArrowUpward : MdArrowDownward} boxSize={3} />}
            </HStack>
        </Th>
    );
});
SortTh.displayName = 'SortTh';
SortTh.propTypes = { col: PropTypes.string, label: PropTypes.string, sortKey: PropTypes.string, sortDir: PropTypes.string, onSort: PropTypes.func };

const Kpi = memo(({ label, value, help, color, isActive, onClick }) => (
    <Box as={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick} textAlign="left"
        p={2.5} borderRadius="lg" borderWidth="1px" borderColor={isActive ? `${color}.400` : 'transparent'}
        bg={isActive ? `${color}.50` : 'transparent'} _hover={onClick ? { borderColor: `${color}.300` } : undefined}
        aria-pressed={onClick ? !!isActive : undefined}>
        <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wide" color="gray.500" fontWeight="700">{label}</Text>
        <Text fontSize="lg" fontWeight="800" color={`${color}.500`} lineHeight="1.2">{value}</Text>
        {help && <Text fontSize="2xs" color="gray.500" noOfLines={1}>{help}</Text>}
    </Box>
));
Kpi.displayName = 'Kpi';
Kpi.propTypes = { label: PropTypes.string, value: PropTypes.node, help: PropTypes.string, color: PropTypes.string, isActive: PropTypes.bool, onClick: PropTypes.func };

const RoadReportSelectorTable = ({
    roads = [], selectedRoadId, onSelectRoad, refreshKey = 0,
    onMapRoad, onRunAI, isRunningAI = false, onViewRoad,
    page, totalPages, totalRoads, onPageChange, isLoadingRoads = false,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [constituencyFilter, setConstituencyFilter] = useState('');
    const [quickFilter, setQuickFilter] = useState('all');
    const [sortKey, setSortKey] = useState('priority');
    const [sortDir, setSortDir] = useState('desc');
    const [summaries, setSummaries] = useState({});
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState(null);
    const [summaryWarnings, setSummaryWarnings] = useState([]);
    const seqRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headBg = useColorModeValue('gray.50', 'gray.900');
    const activeRowBg = useColorModeValue('blue.50', 'blue.900');
    const hoverRowBg = useColorModeValue('gray.50', 'whiteAlpha.100');
    const mutedColor = useColorModeValue('gray.500', 'gray.400');
    const activeNameColor = useColorModeValue('blue.600', 'blue.300');
    const idleDotBg = useColorModeValue('gray.300', 'gray.600');
    const trackBg = useColorModeValue('gray.100', 'whiteAlpha.200');

    // ── server roll-up for the roads on this page ──
    const idKey = useMemo(() => roads.slice(0, MAX_SUMMARY_IDS).map((r) => String(r.id)).join(','), [roads]);
    const idSetRef = useRef(new Set());
    idSetRef.current = new Set(idKey ? idKey.split(',') : []);

    const loadSummaries = useCallback(async (silent = false) => {
        if (!idKey) { setSummaries({}); return; }
        const seq = ++seqRef.current;
        if (!silent) setIsSummaryLoading(true);
        try {
            const res = await apiFetch(`/roads/report-summary?ids=${encodeURIComponent(idKey)}`);
            if (seq !== seqRef.current || !mountedRef.current) return;
            setSummaries(res?.data && typeof res.data === 'object' ? res.data : {});
            setSummaryWarnings(Array.isArray(res?.warnings) ? res.warnings : []);
            setSummaryError(null);
        } catch (err) {
            if (seq !== seqRef.current || !mountedRef.current) return;
            setSummaryError(err?.message || 'Segment / work order roll-up could not be loaded.');
        } finally {
            if (seq === seqRef.current && mountedRef.current) setIsSummaryLoading(false);
        }
    }, [idKey]);

    useEffect(() => { loadSummaries(false); }, [loadSummaries]);
    const firstRefresh = useRef(true);
    useEffect(() => {
        if (firstRefresh.current) { firstRefresh.current = false; return; }
        loadSummaries(true);
    }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const liveFilter = useCallback((name, data) => {
        const p = data?.payload || data || {};
        const rid = p.road_id ?? p.roadId ?? null;
        return rid == null || idSetRef.current.has(String(rid));
    }, []);
    useFlowSenseLive(['roads', 'workOrders'], () => loadSummaries(true), { debounceMs: LIVE_DEBOUNCE_MS, filter: liveFilter, enabled: !!idKey });

    // ── rows ──
    const rows = useMemo(() => roads.map((r) => buildRoadReportRow(r, summaries[String(r.id)] || null)), [roads, summaries]);
    const kpis = useMemo(() => summarizeReportRows(rows), [rows]);

    const constituencies = useMemo(() => {
        const set = new Set();
        roads.forEach((r) => { const c = r.metadata?.constituency; if (c) set.add(c); });
        return Array.from(set).sort();
    }, [roads]);

    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const test = (REPORT_FILTERS[quickFilter] || REPORT_FILTERS.all).test;
        const list = rows.filter((r) => {
            const matchesSearch = !q
                || r.name.toLowerCase().includes(q)
                || (r.code && String(r.code).toLowerCase().includes(q))
                || String(r.id).toLowerCase().includes(q);
            const matchesConstituency = !constituencyFilter || r.constituency === constituencyFilter;
            return matchesSearch && matchesConstituency && test(r);
        });
        return sortReportRows(list, sortKey, sortDir);
    }, [rows, searchQuery, constituencyFilter, quickFilter, sortKey, sortDir]);

    const handleSort = useCallback((col) => {
        if (col === sortKey) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
        setSortKey(col);
        // worst-first defaults: lowest name / PCI / remaining life, highest priority / segments / jobs
        setSortDir(col === 'name' || col === 'pci' || col === 'rul' ? 'asc' : 'desc');
    }, [sortKey]);

    const toggleQuick = useCallback((key) => setQuickFilter((f) => (f === key ? 'all' : key)), []);

    const runNext = useCallback((row) => {
        const intent = row.priority.next.intent;
        if (intent === REPORT_INTENTS.MAP && onMapRoad) { onMapRoad(row.road); return; }
        if (intent === REPORT_INTENTS.RUN_AI && onRunAI) { onRunAI(row.road); return; }
        onSelectRoad(row.road);
    }, [onMapRoad, onRunAI, onSelectRoad]);

    const exportCsv = useCallback(() => {
        try {
            const blob = new Blob([reportRowsToCsv(filteredRows)], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `road-works-report-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch { /* export is best-effort */ }
    }, [filteredRows]);

    const selectedOnPage = rows.some((r) => String(r.id) === String(selectedRoadId));
    const showSummarySkeleton = isSummaryLoading && !Object.keys(summaries).length;
    const colCount = 10;

    return (
        <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} p={4} boxShadow="sm" aria-label="Road works report table">
            {/* header */}
            <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={3}>
                <HStack spacing={2}>
                    <Icon as={FaRoad} color="blue.500" boxSize={4} />
                    <Text fontSize="sm" fontWeight="800">Select Road for Analysis</Text>
                    <Badge colorScheme="blue" borderRadius="full" fontSize="2xs">{filteredRows.length} of {rows.length} on page</Badge>
                    {isSummaryLoading && <Text fontSize="2xs" color={mutedColor}>updating…</Text>}
                </HStack>
                <HStack spacing={2}>
                    <Tooltip hasArrow label="Refresh segments, ML, work orders and inspections">
                        <IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Refresh report table" onClick={() => loadSummaries(false)} isLoading={isSummaryLoading} />
                    </Tooltip>
                    <Button size="xs" variant="outline" leftIcon={<Icon as={MdFileDownload} />} onClick={exportCsv} isDisabled={!filteredRows.length}>Export CSV</Button>
                </HStack>
            </Flex>

            {/* KPIs */}
            <SimpleGrid columns={{ base: 2, md: 4, xl: 7 }} spacing={1} mb={3}>
                <Kpi label="Roads" value={kpis.roads} help={totalRoads ? `${totalRoads} in network` : undefined} color="blue" />
                <Kpi label="Segments" value={showSummarySkeleton ? '…' : kpis.segments} help="sliced stations" color="cyan" />
                <Kpi label="Segment PCI" value={kpis.avgPci ?? '—'} help="ML / survey, weighted" color={pciColor(kpis.avgPci)} />
                <Kpi label="Needs attention" value={kpis.attention} help="AI priority high / critical" color="red"
                    isActive={quickFilter === 'attention'} onClick={() => toggleQuick('attention')} />
                <Kpi label="Open jobs" value={kpis.open} help="linked work orders" color="orange" />
                <Kpi label="QA pending" value={kpis.qaPending} help="completed paving, no audit" color="purple"
                    isActive={quickFilter === 'qa_pending'} onClick={() => toggleQuick('qa_pending')} />
                <Kpi label="ML evaluated" value={`${kpis.mlEvaluated}/${kpis.roads}`} help={kpis.unsegmented ? `${kpis.unsegmented} not segmented` : 'segment model'} color="teal"
                    isActive={quickFilter === 'no_ml'} onClick={() => toggleQuick('no_ml')} />
            </SimpleGrid>

            {/* filters */}
            <Flex gap={2} mb={3} wrap="wrap" align="center">
                <InputGroup size="xs" maxW={{ base: '100%', md: '260px' }}>
                    <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400" /></InputLeftElement>
                    <Input placeholder="Filter by road name, code, or ID..." borderRadius="lg" value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)} aria-label="Filter report roads" />
                </InputGroup>
                {constituencies.length > 0 && (
                    <Select size="xs" borderRadius="lg" maxW="170px" placeholder="All Constituencies" value={constituencyFilter}
                        onChange={(e) => setConstituencyFilter(e.target.value)} aria-label="Constituency filter">
                        {constituencies.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                )}
                <ButtonGroup size="xs" isAttached variant="outline" flexWrap="wrap">
                    {Object.entries(REPORT_FILTERS).map(([key, f]) => (
                        <Button key={key} onClick={() => setQuickFilter(key)} colorScheme={quickFilter === key ? 'blue' : 'gray'}
                            variant={quickFilter === key ? 'solid' : 'outline'} aria-pressed={quickFilter === key}>
                            {f.label}
                        </Button>
                    ))}
                </ButtonGroup>
            </Flex>

            {summaryError && (
                <Alert status="warning" borderRadius="lg" mb={3} py={2}>
                    <AlertIcon />
                    <AlertDescription flex={1} fontSize="xs">Segment / ML / work-order data unavailable: {summaryError}</AlertDescription>
                    <Button size="xs" onClick={() => loadSummaries(false)}>Retry</Button>
                </Alert>
            )}
            {!summaryError && summaryWarnings.length > 0 && (
                <Text fontSize="2xs" color={mutedColor} mb={2}>Partial data: {summaryWarnings.join(', ')}.</Text>
            )}
            {selectedRoadId != null && !selectedOnPage && rows.length > 0 && (
                <Text fontSize="2xs" color={mutedColor} mb={2}>The road in the report below is not on this inventory page.</Text>
            )}

            {/* table */}
            <Box overflow="auto" maxH="420px" borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                <Table size="sm" variant="simple">
                    <Thead bg={headBg} position="sticky" top={0} zIndex={1}>
                        <Tr>
                            <Th w="32px" px={2} />
                            <SortTh col="name" label="Road asset" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                            <Th>Status / condition</Th>
                            <SortTh col="segments" label="Segments" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                            <SortTh col="pci" label="ML condition" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                            <SortTh col="rul" label="AI prediction" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                            <SortTh col="priority" label="AI priority" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                            <SortTh col="work_orders" label="Work orders" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                            <Th>Inspections / QA</Th>
                            <Th w="70px" />
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoadingRoads && !rows.length ? (
                            [0, 1, 2].map((i) => (
                                <Tr key={`sk-${i}`}><Td colSpan={colCount}><Skeleton h="28px" borderRadius="md" /></Td></Tr>
                            ))
                        ) : filteredRows.length === 0 ? (
                            <Tr>
                                <Td colSpan={colCount} textAlign="center" py={6} color={mutedColor} fontSize="xs">
                                    {rows.length ? 'No road assets match the current filters.' : 'No roads loaded on this inventory page.'}
                                </Td>
                            </Tr>
                        ) : filteredRows.map((r) => {
                            const isSelected = String(r.id) === String(selectedRoadId);
                            const rating = r.condition.rating || 'unrated';
                            const seg = r.segments;
                            const lvl = r.priority.level;
                            return (
                                <Tr key={r.id} onClick={() => onSelectRoad(r.road)} cursor="pointer" tabIndex={0}
                                    aria-selected={isSelected} aria-label={`Road ${r.name}`}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectRoad(r.road); } }}
                                    bg={isSelected ? activeRowBg : 'transparent'}
                                    _hover={{ bg: isSelected ? activeRowBg : hoverRowBg }}
                                    transition="background 0.15s ease">
                                    <Td textAlign="center" px={2}>
                                        {isSelected
                                            ? <Icon as={MdCheckCircle} color="blue.500" boxSize={4} aria-label="Selected" />
                                            : <Box w={2} h={2} borderRadius="full" bg={idleDotBg} mx="auto" />}
                                    </Td>

                                    {/* road */}
                                    <Td minW="170px">
                                        <Text fontWeight="800" fontSize="xs" color={isSelected ? activeNameColor : 'inherit'} noOfLines={1}>{r.name}</Text>
                                        <HStack spacing={1} fontSize="2xs" color={mutedColor}>
                                            <Text fontFamily="mono">{r.code || `#${r.id}`}</Text>
                                            {r.constituency && <Text>· {r.constituency}</Text>}
                                        </HStack>
                                        {r.road.location_description && (
                                            <HStack spacing={1}>
                                                <Icon as={MdLocationOn} color="gray.400" boxSize={2.5} />
                                                <Text fontSize="2xs" color={mutedColor} noOfLines={1} maxW="180px">{r.road.location_description}</Text>
                                            </HStack>
                                        )}
                                    </Td>

                                    {/* status / condition */}
                                    <Td>
                                        <VStack align="start" spacing={1}>
                                            <Badge colorScheme={STATUS_SCHEMES[r.status] || 'gray'} fontSize="2xs" borderRadius="md">{String(r.status).replace(/_/g, ' ')}</Badge>
                                            <Badge colorScheme={RATING_SCHEMES[rating] || 'gray'} fontSize="2xs" borderRadius="md" variant="subtle">
                                                {rating}{r.condition.score != null ? ` (${r.condition.score})` : ''}
                                            </Badge>
                                        </VStack>
                                    </Td>

                                    {/* segments */}
                                    <Td>
                                        {showSummarySkeleton ? <Skeleton h="14px" w="60px" /> : seg.count ? (
                                            <Tooltip hasArrow label={`${seg.count} stations · ${seg.unscored_segments} unscored · ${seg.severe_cracking} with severe cracking${seg.max_rutting_mm != null ? ` · max rutting ${seg.max_rutting_mm} mm` : ''}`}>
                                                <Box>
                                                    <Text fontSize="xs" fontWeight="800">{seg.count} seg</Text>
                                                    <Text fontSize="2xs" color={mutedColor}>
                                                        {r.lengthM ? `${(r.lengthM / 1000).toFixed(2)} km` : '—'}
                                                        {seg.potholes ? ` · ${seg.potholes} potholes` : ''}
                                                    </Text>
                                                    {seg.poor_segments > 0 && <Badge colorScheme="red" fontSize="2xs">{seg.poor_segments} poor</Badge>}
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Badge colorScheme={r.hasGeometry ? 'yellow' : 'gray'} fontSize="2xs" variant="outline">
                                                {r.hasGeometry ? 'not segmented' : 'unmapped'}
                                            </Badge>
                                        )}
                                    </Td>

                                    {/* ML condition */}
                                    <Td minW="130px">
                                        {showSummarySkeleton ? <Skeleton h="14px" w="80px" /> : r.ml.pci != null ? (
                                            <Tooltip hasArrow label={[
                                                r.ml.engine ? `Engine: ${r.ml.engine === 'ml' ? 'ML segment model' : r.ml.engine === 'mixed' ? 'ML + rules' : 'FlowSense rules'}` : 'Survey PCI (ML not run yet)',
                                                seg.worst_segment_id ? `Worst ${seg.worst_segment_id}: PCI ${seg.min_pci}` : null,
                                                r.ml.actionLabel ? `Treatment: ${r.ml.actionLabel}` : null,
                                                r.ml.evaluatedAt ? `Evaluated ${relDays(r.ml.evaluatedAt)}` : null,
                                            ].filter(Boolean).join(' · ')}>
                                                <Box>
                                                    <HStack spacing={2}>
                                                        <Progress value={r.ml.pci} size="xs" w="54px" borderRadius="full" colorScheme={pciColor(r.ml.pci)} bg={trackBg} aria-label={`PCI ${r.ml.pci}`} />
                                                        <Text fontSize="xs" fontWeight="800">{r.ml.pci}</Text>
                                                        {r.ml.engine && <Badge fontSize="2xs" colorScheme={r.ml.engine === 'ml' ? 'purple' : 'gray'}>{r.ml.engine}</Badge>}
                                                    </HStack>
                                                    <Text fontSize="2xs" color={mutedColor} noOfLines={1}>
                                                        {r.ml.actionLabel || (seg.min_pci != null ? `min ${seg.min_pci}` : 'no ML run')}
                                                    </Text>
                                                </Box>
                                            </Tooltip>
                                        ) : <Text fontSize="2xs" color={mutedColor}>no segment data</Text>}
                                    </Td>

                                    {/* AI prediction */}
                                    <Td minW="120px">
                                        <Tooltip hasArrow label={r.ai.summary}>
                                            <Box>
                                                <HStack spacing={1}>
                                                    <Icon as={MdAutoAwesome} color="purple.400" boxSize={3} />
                                                    <Text fontSize="xs" fontWeight="800">{r.ai.rulYears != null ? `${r.ai.rulYears}y` : '—'}</Text>
                                                    {r.ai.band && <Badge fontSize="2xs" colorScheme={r.ai.band.color}>{r.ai.band.label}</Badge>}
                                                    {r.ai.floodAlert && <Icon as={MdWaterDrop} color="blue.400" boxSize={3} aria-label="Flood alert" />}
                                                    {/* --- UPDATE (roads v1.4): rain-water forecast on this road */}
                                                    {r.ai.ponding && (
                                                        <Badge fontSize="2xs" colorScheme={r.ai.ponding.band === 'severe' ? 'purple' : r.ai.ponding.band === 'high' ? 'blue' : 'cyan'} aria-label={`Rain water forecast ${r.ai.ponding.band}`}>
                                                            💧 {r.ai.ponding.max_depth_m != null ? `${Math.round(Number(r.ai.ponding.max_depth_m) * 100)} cm` : r.ai.ponding.band}
                                                        </Badge>
                                                    )}
                                                    {/* --- END UPDATE */}
                                                </HStack>
                                                <Text fontSize="2xs" color={mutedColor}>
                                                    {r.ai.source === 'model' ? `RSF model${r.ai.live ? ' · live' : ''}` : r.ai.source === 'estimate' ? 'estimate' : 'no data'}
                                                    {r.ai.insights ? ` · ${r.ai.insights} insight${r.ai.insights > 1 ? 's' : ''}` : ''}
                                                </Text>
                                            </Box>
                                        </Tooltip>
                                    </Td>

                                    {/* AI priority + next action */}
                                    <Td minW="170px">
                                        <Tooltip hasArrow label={r.priority.reasons.map((x) => `+${x.points} ${x.label}`).join(' · ') || 'No risk drivers'}>
                                            <HStack spacing={1} mb={0.5}>
                                                <Badge colorScheme={lvl.color} fontSize="2xs" aria-label={`AI priority ${r.priority.score} ${lvl.label}`}>{r.priority.score} · {lvl.label}</Badge>
                                            </HStack>
                                        </Tooltip>
                                        <Button size="xs" variant="link" colorScheme="blue" fontSize="2xs" whiteSpace="normal" textAlign="left" h="auto"
                                            onClick={(e) => { e.stopPropagation(); runNext(r); }}
                                            isDisabled={r.priority.next.intent === REPORT_INTENTS.RUN_AI && isRunningAI}
                                            aria-label={`Next action for ${r.name}: ${r.priority.next.label}`}>
                                            → {r.priority.next.label}
                                        </Button>
                                    </Td>

                                    {/* work orders */}
                                    <Td>
                                        {showSummarySkeleton ? <Skeleton h="14px" w="50px" /> : (
                                            <VStack align="start" spacing={0.5}>
                                                <Text fontSize="xs" fontWeight="800">{r.workOrders.open} open<Text as="span" fontWeight="400" color={mutedColor}> / {r.workOrders.total}</Text></Text>
                                                <HStack spacing={1} flexWrap="wrap">
                                                    {r.workOrders.critical_open > 0 && <Badge colorScheme="red" fontSize="2xs">{r.workOrders.critical_open} critical</Badge>}
                                                    {r.workOrders.overdue > 0 && <Badge colorScheme="orange" fontSize="2xs">{r.workOrders.overdue} overdue</Badge>}
                                                    {r.workOrders.qa_pending > 0 && <Badge colorScheme="purple" fontSize="2xs">{r.workOrders.qa_pending} QA pending</Badge>}
                                                </HStack>
                                            </VStack>
                                        )}
                                    </Td>

                                    {/* inspections / QA */}
                                    <Td minW="120px">
                                        {showSummarySkeleton ? <Skeleton h="14px" w="70px" /> : (
                                            <VStack align="start" spacing={0.5}>
                                                {r.inspections.last_score != null ? (
                                                    <Badge colorScheme={RATING_SCHEMES[r.inspections.last_rating] || 'gray'} fontSize="2xs">
                                                        {r.inspections.last_score} · {r.inspections.last_rating || '—'}
                                                    </Badge>
                                                ) : <Text fontSize="2xs" color={mutedColor}>no checklist yet</Text>}
                                                <Text fontSize="2xs" color={r.inspections.overdueDays ? 'red.500' : mutedColor}>
                                                    {r.inspections.last_submitted_at ? `last ${relDays(r.inspections.last_submitted_at)}` : ''}
                                                    {r.inspections.overdueDays ? `${r.inspections.last_submitted_at ? ' · ' : ''}${r.inspections.overdueDays}d overdue` : r.inspections.nextDue ? `${r.inspections.last_submitted_at ? ' · ' : ''}due ${relDays(r.inspections.nextDue)}` : ''}
                                                    {r.inspections.drafts ? ` · ${r.inspections.drafts} draft` : ''}
                                                </Text>
                                                {r.qa.latest_status && <Badge colorScheme={QA_SCHEMES[r.qa.latest_status] || 'gray'} fontSize="2xs" variant="subtle">{QA_LABEL[r.qa.latest_status] || r.qa.latest_status}</Badge>}
                                            </VStack>
                                        )}
                                    </Td>

                                    {/* row actions */}
                                    <Td px={1} onClick={(e) => e.stopPropagation()}>
                                        <HStack spacing={0}>
                                            {onViewRoad && (
                                                <Tooltip hasArrow label="Road details & AI prediction">
                                                    <IconButton size="xs" variant="ghost" icon={<Icon as={MdVisibility} />} aria-label={`Details for ${r.name}`} onClick={() => onViewRoad(r.road)} />
                                                </Tooltip>
                                            )}
                                            {onMapRoad && !r.hasGeometry && (
                                                <Tooltip hasArrow label="Draw on GIS map (creates segments)">
                                                    <IconButton size="xs" variant="ghost" colorScheme="orange" icon={<Icon as={MdMap} />} aria-label={`Map ${r.name}`} onClick={() => onMapRoad(r.road)} />
                                                </Tooltip>
                                            )}
                                        </HStack>
                                    </Td>
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>

            {/* paging (drives the shared inventory page) */}
            {onPageChange && totalPages > 1 && (
                <Flex justify="space-between" align="center" mt={2}>
                    <Text fontSize="2xs" color={mutedColor}>Inventory page {page} of {totalPages}{totalRoads ? ` · ${totalRoads} roads` : ''}</Text>
                    <HStack spacing={1}>
                        <IconButton size="xs" variant="ghost" icon={<Icon as={MdChevronLeft} />} aria-label="Previous road page" isDisabled={page <= 1} onClick={() => onPageChange(page - 1)} />
                        <IconButton size="xs" variant="ghost" icon={<Icon as={MdChevronRight} />} aria-label="Next road page" isDisabled={page >= totalPages} onClick={() => onPageChange(page + 1)} />
                    </HStack>
                </Flex>
            )}
        </Box>
    );
};

RoadReportSelectorTable.propTypes = {
    roads: PropTypes.array.isRequired,
    selectedRoadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelectRoad: PropTypes.func.isRequired,
    refreshKey: PropTypes.number,
    onMapRoad: PropTypes.func,
    onRunAI: PropTypes.func,
    isRunningAI: PropTypes.bool,
    onViewRoad: PropTypes.func,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    totalRoads: PropTypes.number,
    onPageChange: PropTypes.func,
    isLoadingRoads: PropTypes.bool,
};

export default memo(RoadReportSelectorTable);
